import {Dropdown} from "primereact/dropdown";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";

import {CLIMATE_RISK_INDEX_CLASSES, getClimateRiskCategoryForValue} from "@reva/shared/climate-risk";

import {selectClimateRiskData} from "../../redux/locationInfoSlice";
import {getTranslationForClimateRiskIndex} from "../../util/climateRiskUtil";
import ParameterFieldContainer from "./ParameterFieldContainer";
import ParameterInputRangeButtons from "./ParameterInputRangeButtons";
import {getResourceColor} from "./paramHooks";

const categoryNumbers = CLIMATE_RISK_INDEX_CLASSES.map((_, categoryNumber) => categoryNumber);

/**
 * "Lokales Hochwasserrisiko" (parameter="location_flood_risk"). Liefert die Spec Ranges für den
 * Parameter, wird analog zu den anderen Range-Parametern (#18858) eine Auswahlbox gerendert;
 * ohne Ranges bleibt rückwärtskompatibel das bisherige Dropdown der 7 Risiko-Kategorien.
 * Persistiert wird in beiden Fällen ein 0-100-Wert (untere Bucket-Grenze). Solange kein manueller
 * Wert gesetzt ist, wird die aus den Klimadaten abgeleitete Flut-Kategorie als Vorschlag
 * angezeigt/hervorgehoben.
 */
export default function ParameterInputFloodRiskCategory({
    parameter,
    parameterType,
    label,
    id,
    parameter_details_def,
    ranges,
}) {
    const {t} = useTranslation();
    const suggestedCategory = useSelector(selectClimateRiskData)?.index_category_flood;
    const suggestedValue = suggestedCategory != null ? CLIMATE_RISK_INDEX_CLASSES[suggestedCategory].lower : null;
    const extraTooltipText = t("climaterisk.flood_risk_input_tooltip");

    if (Array.isArray(ranges) && ranges.length > 0) {
        return (
            <ParameterInputRangeButtons
                parameter={parameter}
                parameterType={parameterType}
                label={label}
                id={id}
                parameter_details_def={parameter_details_def}
                ranges={ranges}
                associatedOutputParamValue={suggestedValue}
                extraTooltipText={extraTooltipText}
            />
        );
    }

    // TODO #18858: Dropdown-Fallback (ab hier) nach dem AVM-Release entfernen, sobald die Spec für
    // location_flood_risk zuverlässig Ranges liefert - dann reicht der obige Zweig allein.
    const options = categoryNumbers.map((categoryNumber) => ({
        value: categoryNumber,
        label: getTranslationForClimateRiskIndex(t, categoryNumber),
    }));

    const eventToValueMapper = (e) => ({
        value: e.target.value != null ? CLIMATE_RISK_INDEX_CLASSES[e.target.value].lower : null,
    });

    return (
        <ParameterFieldContainer
            id={id}
            label={label}
            parameter={parameter}
            parameterType={parameterType}
            parameter_details_def={parameter_details_def}
            showDescription={true}
            eventToValueMapper={eventToValueMapper}
            extraTooltipText={extraTooltipText}
            renderControl={({className, onChange, value, activeParameterType, hasConflict, disabled}) => {
                const selectedCategory = getClimateRiskCategoryForValue(value);
                const color = getResourceColor(activeParameterType, hasConflict);
                const placeholder =
                    suggestedCategory != null &&
                    `${t("object.associatedOutputParamPrefix")} ${getTranslationForClimateRiskIndex(t, suggestedCategory)}`;

                return (
                    <Dropdown
                        id={id}
                        value={selectedCategory}
                        placeholder={placeholder}
                        valueTemplate={
                            selectedCategory != null &&
                            ((option) => <span className={`text-${color}`}>{option.label}</span>)
                        }
                        className={`w-full ${parameter} ${className} border-${hasConflict ? color : ""}`}
                        disabled={disabled}
                        pt={{input: {className: "text-right"}}}
                        options={options}
                        optionLabel="label"
                        onChange={onChange}
                        scrollHeight="300px"
                        data-pr-position="top"
                    />
                );
            }}
        />
    );
}
